import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Waves, LogOut, Plus, Pencil, Trash2, Loader2, ImagePlus, X, ArrowUp, ArrowDown,
  UtensilsCrossed, Settings, ChevronRight, Home, CalendarDays, Ticket, Users,
} from "lucide-react";
import { api, imgUrl, formatPrice } from "@/lib/api";
import { ALLERGENS } from "@/lib/data";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const MENUS = [
  { slug: "mediodia", label: "Carta del Mediodía" },
  { slug: "tardes", label: "Carta de las Tardes" },
];

function ImageUploader({ value, onChange, testid, aspect = "aspect-video" }) {
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Imagen subida");
    } catch { toast.error("Error al subir la imagen"); }
    finally { setBusy(false); }
  };
  return (
    <div className={`relative ${aspect} w-full overflow-hidden rounded-xl border-2 border-dashed border-[#D6D3D1] bg-secondary`}>
      {value ? <img src={imgUrl(value)} alt="" className="h-full w-full object-cover" /> : (
        <div className="flex h-full flex-col items-center justify-center text-[#A8A29E]">
          <ImagePlus className="h-7 w-7" /><span className="mt-1 text-xs">Subir imagen</span>
        </div>
      )}
      <input type="file" accept="image/*" data-testid={testid} onChange={upload}
        className="absolute inset-0 cursor-pointer opacity-0" />
      {busy && <div className="absolute inset-0 flex items-center justify-center bg-white/70"><Loader2 className="h-6 w-6 animate-spin text-[#0369A1]" /></div>}
      {value && <button onClick={() => onChange("")} className="absolute right-2 top-2 rounded-full bg-black/60 p-1.5 text-white hover:bg-black/80"><X className="h-3.5 w-3.5" /></button>}
    </div>
  );
}

const emptyProduct = (menu_slug, category_id) => ({
  menu_slug, category_id, name: "", description: "", prices: [{ label: "", amount: 0 }],
  image: "", allergens: [], available: true, order: 0,
});

function ProductDialog({ open, onClose, initial, menuSlug, onSaved }) {
  const [p, setP] = useState(initial);
  useEffect(() => { setP(initial); }, [initial]);
  if (!p) return null;
  const set = (k, v) => setP((x) => ({ ...x, [k]: v }));
  const toggleAllergen = (key) => set("allergens", p.allergens.includes(key) ? p.allergens.filter((a) => a !== key) : [...p.allergens, key]);
  const setPrice = (i, k, v) => set("prices", p.prices.map((pr, idx) => idx === i ? { ...pr, [k]: k === "amount" ? parseFloat(v || 0) : v } : pr));

  const save = async () => {
    if (!p.name.trim()) return toast.error("El nombre es obligatorio");
    const payload = { ...p, menu_slug: menuSlug, prices: p.prices.filter((pr) => pr.amount || pr.label) };
    try {
      if (p.id) await api.put(`/products/${p.id}`, payload);
      else await api.post("/products", payload);
      toast.success("Producto guardado");
      onSaved(); onClose();
    } catch { toast.error("Error al guardar"); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader><DialogTitle className="font-display text-2xl">{p.id ? "Editar producto" : "Nuevo producto"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <ImageUploader value={p.image} onChange={(v) => set("image", v)} testid="product-image-upload" aspect="aspect-video" />
          <div><Label className="font-semibold">Nombre</Label>
            <Input data-testid="product-name-input" value={p.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5" placeholder="Ej. Gambas al ajillo" /></div>
          <div><Label className="font-semibold">Descripción</Label>
            <Textarea data-testid="product-description-input" value={p.description} onChange={(e) => set("description", e.target.value)} className="mt-1.5" rows={2} placeholder="Ingredientes, elaboración…" /></div>
          <div>
            <Label className="font-semibold">Precios</Label>
            <div className="mt-1.5 space-y-2">
              {p.prices.map((pr, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Input value={pr.label} onChange={(e) => setPrice(i, "label", e.target.value)} placeholder="Etiqueta (Ración, Media…)" className="flex-1" />
                  <Input type="number" step="0.5" value={pr.amount} onChange={(e) => setPrice(i, "amount", e.target.value)} placeholder="0.00" className="w-24" />
                  <span className="text-sm text-[#78716C]">€</span>
                  {p.prices.length > 1 && <button onClick={() => set("prices", p.prices.filter((_, idx) => idx !== i))} className="text-[#DC2626]"><X className="h-4 w-4" /></button>}
                </div>
              ))}
              <button data-testid="add-price-button" onClick={() => set("prices", [...p.prices, { label: "", amount: 0 }])}
                className="inline-flex items-center gap-1 text-sm font-semibold text-[#0369A1]"><Plus className="h-4 w-4" /> Añadir precio</button>
            </div>
          </div>
          <div>
            <Label className="font-semibold">Alérgenos</Label>
            <div className="mt-2 flex flex-wrap gap-2">
              {ALLERGENS.map((a) => {
                const Icon = a.icon; const on = p.allergens.includes(a.key);
                return (
                  <button key={a.key} data-testid={`allergen-toggle-${a.key}`} onClick={() => toggleAllergen(a.key)}
                    className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${on ? "border-transparent text-white" : "border-[#E7E5E4] text-[#57534E] hover:bg-secondary"}`}
                    style={on ? { backgroundColor: a.color } : {}}>
                    <Icon className="h-3.5 w-3.5" /> {a.es}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="flex items-center justify-between rounded-xl bg-secondary px-4 py-3">
            <Label className="font-semibold">Disponible</Label>
            <Switch data-testid="product-available-switch" checked={p.available} onCheckedChange={(v) => set("available", v)} />
          </div>
        </div>
        <DialogFooter>
          <button onClick={onClose} className="rounded-full px-5 py-2.5 font-semibold text-[#57534E] hover:bg-secondary">Cancelar</button>
          <button data-testid="save-product-button" onClick={save} className="rounded-full bg-[#0369A1] px-6 py-2.5 font-bold text-white hover:bg-[#075985]">Guardar</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SettingsTab({ settings, reload }) {
  const [s, setS] = useState(settings);
  useEffect(() => { setS(settings); }, [settings]);
  const set = (k, v) => setS((x) => ({ ...x, [k]: v }));
  const save = async () => {
    try { await api.put("/settings", s); toast.success("Ajustes guardados"); reload(); }
    catch { toast.error("Error al guardar ajustes"); }
  };
  const gallery = s.gallery || [];
  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2">
        <div><Label className="font-semibold">Nombre del restaurante</Label>
          <Input data-testid="settings-name-input" value={s.restaurant_name || ""} onChange={(e) => set("restaurant_name", e.target.value)} className="mt-1.5" /></div>
        <div><Label className="font-semibold">Eslogan (pie de página)</Label>
          <Input data-testid="settings-tagline-input" value={s.tagline || ""} onChange={(e) => set("tagline", e.target.value)} className="mt-1.5" /></div>
      </div>
      <div className="rounded-2xl bg-white p-5 ring-1 ring-[#E7E5E4]">
        <h3 className="font-display mb-1 text-lg font-bold text-[#1C1917]">Datos legales (para la pasarela de pago)</h3>
        <p className="mb-4 text-sm text-[#78716C]">Deben coincidir EXACTAMENTE con los del contrato de tu TPV Virtual. Se muestran en el aviso legal, privacidad y términos.</p>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Razón social" value={s.legal_name} onChange={(v) => set("legal_name", v)} testid="legal-name-input" />
          <Field label="CIF / NIF" value={s.legal_cif} onChange={(v) => set("legal_cif", v)} testid="legal-cif-input" />
          <Field label="Domicilio fiscal" value={s.legal_address} onChange={(v) => set("legal_address", v)} testid="legal-address-input" />
          <Field label="Email de contacto legal" value={s.legal_email} onChange={(v) => set("legal_email", v)} testid="legal-email-input" />
          <Field label="Teléfono (opcional)" value={s.legal_phone} onChange={(v) => set("legal_phone", v)} testid="legal-phone-input" />
        </div>
      </div>
      <div className="rounded-2xl bg-white p-5 ring-1 ring-[#E7E5E4]">
        <h3 className="font-display mb-1 text-lg font-bold text-[#1C1917]">Logotipos y favicon</h3>
        <p className="mb-4 text-sm text-[#78716C]">Cambia el logo de la web y el icono de la pestaña (favicon) cuando quieras.</p>
        <div className="grid gap-4 sm:grid-cols-3">
          <div>
            <Label className="mb-1.5 block font-semibold">Logo (fondos claros)</Label>
            <div className="rounded-xl bg-[#F5F7F9] p-2"><ImageUploader value={s.logo} onChange={(v) => set("logo", v)} testid="logo-upload" aspect="aspect-square" /></div>
            <p className="mt-1 text-xs text-[#A8A29E]">Cabecera de cartas, panel y login.</p>
          </div>
          <div>
            <Label className="mb-1.5 block font-semibold">Logo (fondos oscuros)</Label>
            <div className="rounded-xl bg-[#075985] p-2"><ImageUploader value={s.logo_light} onChange={(v) => set("logo_light", v)} testid="logo-light-upload" aspect="aspect-square" /></div>
            <p className="mt-1 text-xs text-[#A8A29E]">Barra superior del inicio.</p>
          </div>
          <div>
            <Label className="mb-1.5 block font-semibold">Favicon</Label>
            <div className="rounded-xl bg-[#F5F7F9] p-2"><ImageUploader value={s.favicon} onChange={(v) => set("favicon", v)} testid="favicon-upload" aspect="aspect-square" /></div>
            <p className="mt-1 text-xs text-[#A8A29E]">Icono de la pestaña del navegador.</p>
          </div>
        </div>
      </div>
      <div>
        <Label className="mb-1.5 block font-semibold">Galería del establecimiento</Label>
        <p className="mb-2 text-sm text-[#78716C]">Estas imágenes aparecen en la sección "Galería" del inicio.</p>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {gallery.map((g, i) => (
            <div key={i} className="relative aspect-square overflow-hidden rounded-xl">
              <img src={imgUrl(g)} alt="" className="h-full w-full object-cover" />
              <button onClick={() => set("gallery", gallery.filter((_, idx) => idx !== i))} className="absolute right-1.5 top-1.5 rounded-full bg-black/60 p-1 text-white"><X className="h-3.5 w-3.5" /></button>
            </div>
          ))}
          <div className="aspect-square">
            <ImageUploader value="" onChange={(v) => v && set("gallery", [...gallery, v])} testid="gallery-upload" aspect="aspect-square" />
          </div>
        </div>
      </div>
      <button data-testid="save-settings-button" onClick={save} className="rounded-full bg-[#EA580C] px-7 py-3 font-bold text-white hover:bg-[#C2410C]">Guardar ajustes</button>
    </div>
  );
}

const Field = ({ label, value, onChange, testid, textarea, rows = 3, placeholder }) => (
  <div>
    <Label className="font-semibold">{label}</Label>
    {textarea
      ? <Textarea data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)} rows={rows} placeholder={placeholder} className="mt-1.5" />
      : <Input data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="mt-1.5" />}
  </div>
);

const Block = ({ title, children }) => (
  <div className="rounded-2xl bg-white p-5 ring-1 ring-[#E7E5E4]">
    <h3 className="font-display mb-4 text-lg font-bold text-[#1C1917]">{title}</h3>
    <div className="space-y-4">{children}</div>
  </div>
);

function InicioTab({ settings, reload }) {
  const [s, setS] = useState(settings);
  useEffect(() => { setS(settings); }, [settings]);
  const set = (k, v) => setS((x) => ({ ...x, [k]: v }));
  const save = async () => {
    try { await api.put("/settings", s); toast.success("Inicio actualizado"); reload(); }
    catch { toast.error("Error al guardar"); }
  };
  return (
    <div className="space-y-5">
      <Block title="Portada principal (Hero)">
        <Field label="Título" value={s.hero_title} onChange={(v) => set("hero_title", v)} testid="hero-title-input" />
        <Field label="Subtítulo" value={s.hero_subtitle} onChange={(v) => set("hero_subtitle", v)} testid="hero-subtitle-input" textarea rows={2} />
        <div><Label className="mb-1.5 block font-semibold">Imagen de fondo</Label>
          <ImageUploader value={s.hero_bg} onChange={(v) => set("hero_bg", v)} testid="hero-bg-upload" /></div>
      </Block>
      <Block title="Sección Cartas">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Título de la sección" value={s.cartas_section_title} onChange={(v) => set("cartas_section_title", v)} testid="cartas-title-input" />
          <Field label="Texto de la sección" value={s.cartas_section_text} onChange={(v) => set("cartas_section_text", v)} testid="cartas-text-input" />
          <Field label="Nombre Carta Mediodía" value={s.mediodia_title} onChange={(v) => set("mediodia_title", v)} testid="mediodia-title-input" />
          <Field label="Nombre Carta Tardes" value={s.tardes_title} onChange={(v) => set("tardes_title", v)} testid="tardes-title-input" />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div><Label className="mb-1.5 block font-semibold">Portada Mediodía</Label>
            <ImageUploader value={s.mediodia_hero} onChange={(v) => set("mediodia_hero", v)} testid="hero-mediodia-upload" /></div>
          <div><Label className="mb-1.5 block font-semibold">Portada Tardes</Label>
            <ImageUploader value={s.tardes_hero} onChange={(v) => set("tardes_hero", v)} testid="hero-tardes-upload" /></div>
        </div>
      </Block>
      <Block title="Sección Eventos y Galería">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Título Eventos" value={s.eventos_section_title} onChange={(v) => set("eventos_section_title", v)} testid="eventos-title-input" />
          <Field label="Texto Eventos" value={s.eventos_section_text} onChange={(v) => set("eventos_section_text", v)} testid="eventos-text-input" />
          <Field label="Título Galería" value={s.gallery_section_title} onChange={(v) => set("gallery_section_title", v)} testid="gallery-title-input" />
        </div>
      </Block>
      <Block title="Sobre Nosotros">
        <Field label="Título" value={s.about_title} onChange={(v) => set("about_title", v)} testid="about-title-input" />
        <Field label="Texto" value={s.about_text} onChange={(v) => set("about_text", v)} testid="about-text-input" textarea rows={4} />
      </Block>
      <Block title="Ubicación / Cómo llegar">
        <Field label="Título" value={s.location_title} onChange={(v) => set("location_title", v)} testid="location-title-input" />
        <Field label="Texto" value={s.location_text} onChange={(v) => set("location_text", v)} testid="location-text-input" textarea rows={2} />
        <Field label="Dirección" value={s.location_address} onChange={(v) => set("location_address", v)} testid="location-address-input" />
        <Field label="URL del mapa (Google Maps → Compartir → Insertar → copia el enlace src)" value={s.location_map_url} onChange={(v) => set("location_map_url", v)} testid="location-map-input" placeholder="https://www.google.com/maps/embed?..." />
      </Block>
      <button data-testid="save-inicio-button" onClick={save} className="rounded-full bg-[#EA580C] px-7 py-3 font-bold text-white hover:bg-[#C2410C]">Guardar cambios</button>
    </div>
  );
}

const emptyEvent = () => ({ name: "", description: "", image: "", price: 0, total_tickets: 50, event_date: "", active: true });

function EventDialog({ open, onClose, initial, onSaved }) {
  const [e, setE] = useState(initial);
  useEffect(() => { setE(initial); }, [initial]);
  if (!e) return null;
  const set = (k, v) => setE((x) => ({ ...x, [k]: v }));
  const save = async () => {
    if (!e.name.trim()) return toast.error("El nombre es obligatorio");
    const payload = { ...e, price: Number(e.price) || 0, total_tickets: Number(e.total_tickets) || 0 };
    try {
      if (e.id) await api.put(`/events/${e.id}`, payload);
      else await api.post("/events", payload);
      toast.success("Evento guardado"); onSaved(); onClose();
    } catch { toast.error("Error al guardar el evento"); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader><DialogTitle className="font-display text-2xl">{e.id ? "Editar evento" : "Nuevo evento"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <ImageUploader value={e.image} onChange={(v) => set("image", v)} testid="event-image-upload" />
          <div><Label className="font-semibold">Nombre del evento</Label>
            <Input data-testid="event-name-input" value={e.name} onChange={(ev) => set("name", ev.target.value)} className="mt-1.5" placeholder="Ej. Noche de Flamenco" /></div>
          <div><Label className="font-semibold">Descripción</Label>
            <Textarea data-testid="event-description-input" value={e.description} onChange={(ev) => set("description", ev.target.value)} rows={3} className="mt-1.5" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label className="font-semibold">Precio (€)</Label>
              <Input data-testid="event-price-input" type="number" step="0.5" value={e.price} onChange={(ev) => set("price", ev.target.value)} className="mt-1.5" /></div>
            <div><Label className="font-semibold">Nº de entradas</Label>
              <Input data-testid="event-tickets-input" type="number" value={e.total_tickets} onChange={(ev) => set("total_tickets", ev.target.value)} className="mt-1.5" /></div>
          </div>
          <div><Label className="font-semibold">Fecha (texto libre)</Label>
            <Input data-testid="event-date-input" value={e.event_date} onChange={(ev) => set("event_date", ev.target.value)} className="mt-1.5" placeholder="Ej. Sábado 15 de agosto, 21:00" /></div>
          <div className="flex items-center justify-between rounded-xl bg-secondary px-4 py-3">
            <Label className="font-semibold">Publicado (visible en la web)</Label>
            <Switch data-testid="event-active-switch" checked={e.active} onCheckedChange={(v) => set("active", v)} />
          </div>
        </div>
        <DialogFooter>
          <button onClick={onClose} className="rounded-full px-5 py-2.5 font-semibold text-[#57534E] hover:bg-secondary">Cancelar</button>
          <button data-testid="save-event-button" onClick={save} className="rounded-full bg-[#0369A1] px-6 py-2.5 font-bold text-white hover:bg-[#075985]">Guardar</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EventsTab() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState({ open: false, initial: null });
  const [confirmId, setConfirmId] = useState(null);
  const [resView, setResView] = useState(null);
  const [reservations, setReservations] = useState([]);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/events", { params: { include_inactive: true } }); setEvents(r.data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openReservations = async (ev) => {
    setResView(ev);
    const r = await api.get("/reservations", { params: { event_id: ev.id } });
    setReservations(r.data);
  };
  const del = async () => { await api.delete(`/events/${confirmId}`); setConfirmId(null); load(); toast.success("Evento eliminado"); };

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <p className="text-sm text-[#78716C]">Publica eventos y gestiona las reservas. Cuando se agotan las entradas, la venta se cierra sola.</p>
        <button data-testid="add-event-button" onClick={() => setDialog({ open: true, initial: emptyEvent() })}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-[#EA580C] px-5 py-2.5 font-bold text-white hover:bg-[#C2410C]"><Plus className="h-4 w-4" /> Nuevo evento</button>
      </div>
      {loading ? (
        <div className="flex justify-center py-20 text-[#0369A1]"><Loader2 className="h-8 w-8 animate-spin" /></div>
      ) : events.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[#D6D3D1] py-16 text-center text-[#78716C]">Aún no hay eventos. Crea el primero.</div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {events.map((ev) => (
            <div key={ev.id} data-testid={`admin-event-${ev.id}`} className="overflow-hidden rounded-2xl bg-white ring-1 ring-[#E7E5E4]">
              <div className="flex gap-4 p-4">
                {ev.image ? <img src={imgUrl(ev.image)} alt="" className="h-20 w-20 shrink-0 rounded-xl object-cover" /> : <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-xl bg-secondary text-[#D6D3D1]"><Ticket className="h-6 w-6" /></div>}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="truncate font-display text-lg font-bold text-[#1C1917]">{ev.name}</h3>
                    {!ev.active && <span className="rounded-full bg-secondary px-2 py-0.5 text-xs font-semibold text-[#78716C]">Oculto</span>}
                  </div>
                  {ev.event_date ? <p className="text-xs text-[#78716C]">{ev.event_date}</p> : null}
                  <p className="mt-1 text-sm font-semibold text-[#0369A1]">{ev.price ? formatPrice(ev.price) : "Entrada libre"} · {ev.sold_tickets}/{ev.total_tickets} vendidas</p>
                  {ev.sold_out && <span className="mt-1 inline-block rounded-full bg-[#FEE2E2] px-2 py-0.5 text-xs font-bold text-[#DC2626]">Agotado</span>}
                </div>
              </div>
              <div className="flex divide-x divide-[#F0EDE7] border-t border-[#F0EDE7] text-sm font-semibold">
                <button data-testid={`event-reservations-${ev.id}`} onClick={() => openReservations(ev)} className="flex flex-1 items-center justify-center gap-1.5 py-3 text-[#0369A1] hover:bg-[#F0F9FF]"><Users className="h-4 w-4" /> Reservas</button>
                <button data-testid={`edit-event-${ev.id}`} onClick={() => setDialog({ open: true, initial: { ...ev } })} className="flex flex-1 items-center justify-center gap-1.5 py-3 text-[#57534E] hover:bg-secondary"><Pencil className="h-4 w-4" /> Editar</button>
                <button data-testid={`delete-event-${ev.id}`} onClick={() => setConfirmId(ev.id)} className="flex flex-1 items-center justify-center gap-1.5 py-3 text-[#DC2626] hover:bg-[#FEE2E2]"><Trash2 className="h-4 w-4" /> Borrar</button>
              </div>
            </div>
          ))}
        </div>
      )}

      <EventDialog open={dialog.open} initial={dialog.initial} onClose={() => setDialog({ open: false, initial: null })} onSaved={load} />

      <Dialog open={!!resView} onOpenChange={(o) => !o && setResView(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-display text-2xl">Reservas · {resView?.name}</DialogTitle></DialogHeader>
          {reservations.length === 0 ? <p className="py-6 text-center text-[#78716C]">Todavía no hay reservas.</p> : (
            <div className="space-y-2">
              {reservations.map((r) => (
                <div key={r.id} className="rounded-xl bg-secondary p-3">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-[#1C1917]">{r.name}</span>
                    <span className="rounded-full bg-white px-2.5 py-0.5 text-sm font-bold text-[#0369A1]">{r.quantity} pers.</span>
                  </div>
                  <p className="text-sm text-[#57534E]">{r.phone} · {r.email}</p>
                  {r.notes ? <p className="mt-1 text-sm italic text-[#78716C]">"{r.notes}"</p> : null}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmId} onOpenChange={(o) => !o && setConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>¿Eliminar este evento?</AlertDialogTitle>
            <AlertDialogDescription>Se eliminarán también sus reservas. Esta acción no se puede deshacer.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-event-button" onClick={del} className="bg-[#DC2626] hover:bg-[#B91C1C]">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("inicio");
  const [menuSlug, setMenuSlug] = useState("mediodia");
  const [cats, setCats] = useState([]);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [prodDialog, setProdDialog] = useState({ open: false, initial: null });
  const [catName, setCatName] = useState("");
  const [editingCat, setEditingCat] = useState(null);
  const [confirm, setConfirm] = useState(null);

  const loadMenu = useCallback(async () => {
    setLoading(true);
    try {
      const [m, s] = await Promise.all([api.get(`/menu/${menuSlug}`), api.get("/settings")]);
      setCats(m.data.categories); setSettings(s.data);
    } catch { toast.error("Error al cargar"); }
    finally { setLoading(false); }
  }, [menuSlug]);

  useEffect(() => {
    api.get("/auth/me").catch(() => navigate("/admin"));
  }, [navigate]);
  useEffect(() => { loadMenu(); }, [loadMenu]);

  const logout = () => { localStorage.removeItem("salitre_token"); navigate("/admin"); };

  const addCategory = async () => {
    if (!catName.trim()) return;
    await api.post("/categories", { menu_slug: menuSlug, name: catName.trim(), order: cats.length });
    setCatName(""); loadMenu(); toast.success("Categoría añadida");
  };
  const saveCatName = async (c) => {
    await api.put(`/categories/${c.id}`, { menu_slug: menuSlug, name: editingCat.name, order: c.order });
    setEditingCat(null); loadMenu();
  };
  const doDelete = async () => {
    const { type, id } = confirm;
    await api.delete(`/${type}/${id}`);
    setConfirm(null); loadMenu();
    toast.success("Eliminado");
  };

  const moveCategory = async (index, dir) => {
    const to = index + dir;
    if (to < 0 || to >= cats.length) return;
    const arr = [...cats];
    [arr[index], arr[to]] = [arr[to], arr[index]];
    setCats(arr);
    await api.post("/categories/reorder", { ids: arr.map((c) => c.id) });
  };

  const moveProduct = async (cat, index, dir) => {
    const to = index + dir;
    if (to < 0 || to >= cat.products.length) return;
    const prods = [...cat.products];
    [prods[index], prods[to]] = [prods[to], prods[index]];
    setCats(cats.map((c) => (c.id === cat.id ? { ...c, products: prods } : c)));
    await api.post("/products/reorder", { ids: prods.map((p) => p.id) });
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <header className="sticky top-0 z-30 border-b border-[#E7E5E4] bg-white/85 backdrop-blur-xl">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-3.5">
          <div className="flex items-center gap-2 text-[#075985]">
            {settings.logo ? <img src={imgUrl(settings.logo)} alt="" className="h-9 w-auto object-contain" /> : <Waves className="h-6 w-6" />}
            <span className="font-display text-xl font-bold">Panel Salitre</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => window.open("/", "_blank")} className="hidden rounded-full bg-secondary px-4 py-2 text-sm font-semibold text-[#57534E] hover:bg-[#E0F2FE] sm:block">Ver web</button>
            <button data-testid="logout-button" onClick={logout} className="inline-flex items-center gap-1.5 rounded-full bg-secondary px-4 py-2 text-sm font-semibold text-[#57534E] hover:bg-[#FEE2E2] hover:text-[#DC2626]"><LogOut className="h-4 w-4" /> Salir</button>
          </div>
        </div>
        <div className="no-scrollbar mx-auto flex max-w-5xl gap-1 overflow-x-auto px-4">
          {[["inicio", "Inicio", Home], ["cartas", "Cartas", UtensilsCrossed], ["eventos", "Eventos", CalendarDays], ["ajustes", "Ajustes", Settings]].map(([k, l, Icon]) => (
            <button key={k} data-testid={`tab-${k}`} onClick={() => setTab(k)}
              className={`inline-flex shrink-0 items-center gap-1.5 border-b-2 px-4 py-3 text-sm font-bold transition-colors ${tab === k ? "border-[#EA580C] text-[#1C1917]" : "border-transparent text-[#A8A29E] hover:text-[#57534E]"}`}>
              <Icon className="h-4 w-4" /> {l}
            </button>
          ))}
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-8">
        {tab === "inicio" ? (
          <InicioTab settings={settings} reload={loadMenu} />
        ) : tab === "eventos" ? (
          <EventsTab />
        ) : tab === "ajustes" ? (
          <SettingsTab settings={settings} reload={loadMenu} />
        ) : (
          <>
            <div className="mb-6 flex flex-wrap gap-2">
              {MENUS.map((m) => (
                <button key={m.slug} data-testid={`select-menu-${m.slug}`} onClick={() => setMenuSlug(m.slug)}
                  className={`rounded-full px-5 py-2.5 text-sm font-bold transition-colors ${menuSlug === m.slug ? "bg-[#0369A1] text-white" : "bg-white text-[#57534E] ring-1 ring-[#E7E5E4] hover:bg-secondary"}`}>
                  {m.label}
                </button>
              ))}
            </div>

            <div className="mb-6 flex gap-2">
              <Input data-testid="new-category-input" value={catName} onChange={(e) => setCatName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addCategory()} placeholder="Nueva categoría (ej. Entrantes)" className="h-11 max-w-sm" />
              <button data-testid="add-category-button" onClick={addCategory} className="inline-flex items-center gap-1.5 rounded-full bg-[#EA580C] px-5 font-bold text-white hover:bg-[#C2410C]"><Plus className="h-4 w-4" /> Categoría</button>
            </div>

            {loading ? (
              <div className="flex justify-center py-20 text-[#0369A1]"><Loader2 className="h-8 w-8 animate-spin" /></div>
            ) : cats.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-[#D6D3D1] py-16 text-center text-[#78716C]">
                Aún no hay categorías en esta carta. Crea la primera arriba.
              </div>
            ) : (
              <div className="space-y-5">
                {cats.map((c, ci) => (
                  <div key={c.id} className="rounded-2xl bg-white ring-1 ring-[#E7E5E4]">
                    <div className="flex items-center justify-between gap-2 border-b border-[#F0EDE7] px-3 py-3 sm:px-5 sm:py-4">
                      {editingCat?.id === c.id ? (
                        <div className="flex flex-1 flex-wrap items-center gap-2">
                          <Input value={editingCat.name} onChange={(e) => setEditingCat({ ...editingCat, name: e.target.value })} className="h-9 min-w-0 flex-1 sm:max-w-xs" autoFocus />
                          <button onClick={() => saveCatName(c)} className="rounded-full bg-[#0369A1] px-4 py-1.5 text-sm font-semibold text-white">Guardar</button>
                          <button onClick={() => setEditingCat(null)} className="text-sm text-[#78716C]">Cancelar</button>
                        </div>
                      ) : (
                        <div className="flex min-w-0 items-center gap-1.5">
                          <div className="flex flex-col">
                            <button data-testid={`cat-up-${c.id}`} onClick={() => moveCategory(ci, -1)} disabled={ci === 0}
                              className="rounded p-0.5 text-[#78716C] hover:bg-secondary disabled:opacity-25"><ArrowUp className="h-4 w-4" /></button>
                            <button data-testid={`cat-down-${c.id}`} onClick={() => moveCategory(ci, 1)} disabled={ci === cats.length - 1}
                              className="rounded p-0.5 text-[#78716C] hover:bg-secondary disabled:opacity-25"><ArrowDown className="h-4 w-4" /></button>
                          </div>
                          <h3 className="truncate font-display text-lg font-bold text-[#1C1917] sm:text-xl">{c.name}</h3>
                          <span className="shrink-0 rounded-full bg-secondary px-2 py-0.5 text-xs font-semibold text-[#78716C]">{c.products.length}</span>
                        </div>
                      )}
                      <div className="flex shrink-0 items-center gap-1">
                        <button data-testid={`edit-category-${c.id}`} onClick={() => setEditingCat({ id: c.id, name: c.name })} className="rounded-lg p-2 text-[#57534E] hover:bg-secondary"><Pencil className="h-4 w-4" /></button>
                        <button data-testid={`delete-category-${c.id}`} onClick={() => setConfirm({ type: "categories", id: c.id, label: c.name })} className="rounded-lg p-2 text-[#DC2626] hover:bg-[#FEE2E2]"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </div>
                    <div className="divide-y divide-[#F5F2EC]">
                      {c.products.map((p, pi) => (
                        <div key={p.id} className="flex items-center gap-2 px-3 py-3 sm:gap-3 sm:px-5">
                          <div className="flex flex-col">
                            <button data-testid={`prod-up-${p.id}`} onClick={() => moveProduct(c, pi, -1)} disabled={pi === 0}
                              className="rounded p-0.5 text-[#A8A29E] hover:bg-secondary disabled:opacity-25"><ArrowUp className="h-3.5 w-3.5" /></button>
                            <button data-testid={`prod-down-${p.id}`} onClick={() => moveProduct(c, pi, 1)} disabled={pi === c.products.length - 1}
                              className="rounded p-0.5 text-[#A8A29E] hover:bg-secondary disabled:opacity-25"><ArrowDown className="h-3.5 w-3.5" /></button>
                          </div>
                          {p.image ? <img src={imgUrl(p.image)} alt="" className="h-11 w-11 shrink-0 rounded-lg object-cover" /> : <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-secondary text-[#D6D3D1]"><UtensilsCrossed className="h-4 w-4" /></div>}
                          <div className="min-w-0 flex-1">
                            <p className="truncate font-semibold text-[#1C1917]">{p.name} {p.available === false && <span className="text-xs font-normal text-[#DC2626]">(no disp.)</span>}</p>
                            <p className="truncate text-sm text-[#78716C]">{(p.prices || []).map((pr) => `${pr.label ? pr.label + " " : ""}${formatPrice(pr.amount)}`).join(" · ")}</p>
                          </div>
                          <button data-testid={`edit-product-${p.id}`} onClick={() => setProdDialog({ open: true, initial: { ...p } })} className="rounded-lg p-2 text-[#57534E] hover:bg-secondary"><Pencil className="h-4 w-4" /></button>
                          <button data-testid={`delete-product-${p.id}`} onClick={() => setConfirm({ type: "products", id: p.id, label: p.name })} className="rounded-lg p-2 text-[#DC2626] hover:bg-[#FEE2E2]"><Trash2 className="h-4 w-4" /></button>
                        </div>
                      ))}
                    </div>
                    <button data-testid={`add-product-${c.id}`} onClick={() => setProdDialog({ open: true, initial: emptyProduct(menuSlug, c.id) })}
                      className="flex w-full items-center gap-2 px-4 py-3.5 text-sm font-bold text-[#0369A1] hover:bg-[#F0F9FF] sm:px-5">
                      <Plus className="h-4 w-4" /> Añadir producto <ChevronRight className="ml-auto h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </main>

      <ProductDialog open={prodDialog.open} initial={prodDialog.initial} menuSlug={menuSlug}
        onClose={() => setProdDialog({ open: false, initial: null })} onSaved={loadMenu} />

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar "{confirm?.label}"?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirm?.type === "categories" ? "Se eliminará la categoría y todos sus productos." : "Esta acción no se puede deshacer."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-button" onClick={doDelete} className="bg-[#DC2626] hover:bg-[#B91C1C]">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
